// DEPENDENCIES ---------------------------------------------------------------- //

import React, { useMemo } from 'react';

import { Arinvoices } from '@gql/base-types';
import { useGetInvoiceSidebarQuery } from '../queries/getInvoiceSidebar.query.generated';
import { useGetInvoiceSidebarAmountDueQuery } from '../queries/getInvoiceSidebarAmountDue.query.generated';

import { css } from '@emotion/css';
import { Alert, LinearProgress, Theme, useTheme } from '@mui/material';

import { getDefaultStart, getDefaultEnd } from '@features/invoices/services/calcService';

import InvoiceSidebarFilter from '@features/invoices/components/InvoiceSidebarFilter';
import InvoiceSidebarList from '@features/invoices/components/InvoiceSidebarList';

// TYPES ---------------------------------------------------------------- //

type InvoiceSidebarProps = {
  rooftopId?: number;
  selectedInvoiceId?: number;
};

// COMPONENT ---------------------------------------------------------------- //

const InvoiceSidebar = ({ rooftopId, selectedInvoiceId }: InvoiceSidebarProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const [start, setStart] = React.useState<string>(getDefaultStart());
  const [end, setEnd] = React.useState<string>(getDefaultEnd());

  // The list itself stays in Apollo's cache.
  const { data, loading, error } = useGetInvoiceSidebarQuery({
    variables: { rooftopId, start, end },
  });
  // The rows each Amount Due is worked out from skip the cache: a program
  // invoice of thousands of moves brings several MB, and while they sat in the
  // cache every other cache write re-read them. The invoice screen refetches
  // this when a payment or credit changes.
  const { data: amountDueData, error: amountDueError } = useGetInvoiceSidebarAmountDueQuery({
    variables: { rooftopId, start, end },
    fetchPolicy: 'no-cache',
  });
  const amountDueRows = useMemo(
    () => new Map((amountDueData?.arinvoices ?? []).map(invoice => [Number(invoice.id), invoice.amount_due_argroups])),
    [amountDueData]
  );
  const amountDueArgroupsFor = (invoiceId: number) =>
    amountDueError ? ('unavailable' as const) : amountDueData ? amountDueRows.get(invoiceId) ?? [] : ('loading' as const);

  const invoices: Arinvoices[] = data?.arinvoices || [];

  return (
    <div className={cls.sidebar}>
      <InvoiceSidebarFilter start={start} end={end} setStart={setStart} setEnd={setEnd} />

      {loading ? <LinearProgress /> : null}

      {error ? <Alert severity='error'>Error fetching invoices</Alert> : null}

      {!loading && !error ? <InvoiceSidebarList invoices={invoices} selectedInvoiceId={selectedInvoiceId} amountDueArgroupsFor={amountDueArgroupsFor} /> : null}
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  sidebar: css`
    position: relative;
    flex: 1;
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default InvoiceSidebar;
